import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { API, API_BASEURL, buildUrl, SWR_CONFIG } from "../../config";
import useGetSWR from "../../hooks/useGetSWR";
import { fetcherPost } from "../../utils/fetcher";
import { getToken } from "../../utils/auth";

interface PendingLegalText {
    id: string;
    version: number;
    content: string;
    type: string;
    created_at: string;
    created_by: string;
}

interface PendingResponse {
    success: boolean;
    data: PendingLegalText[];
}

const TYPE_LABELS: Record<string, string> = {
    terms_and_conditions: "Terms and Conditions",
    privacy_policy: "Privacy Policy",
};

export const LegalTextAcceptanceModal = () => {
    const auth0 = useAuth0();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAccepting, setIsAccepting] = useState(false);
    const [pendingTexts, setPendingTexts] = useState<PendingLegalText[]>([]);

    const url = buildUrl(API.LEGAL_TEXTS_PENDING, {}, API_BASEURL, {});
    const { data, mutate } = useGetSWR<PendingResponse>(url, SWR_CONFIG.FORMS);

    useEffect(() => {
        if (data?.data && data.data.length > 0) {
            setPendingTexts(data.data);
            setCurrentIndex(0);
        } else {
            setPendingTexts([]);
        }
    }, [data]);

    const handleAccept = async () => {
        const currentText = pendingTexts[currentIndex];
        if (!currentText) return;

        setIsAccepting(true);
        try {
            const postUrl = buildUrl(API.LEGAL_TEXTS_ACCEPT, {}, API_BASEURL);
            const token = await getToken(auth0);
            const [, error] = await fetcherPost(postUrl, token, {
                legal_text_id: currentText.id,
            });

            if (!error) {
                if (currentIndex < pendingTexts.length - 1) {
                    setCurrentIndex((prev) => prev + 1);
                } else {
                    setPendingTexts([]);
                    mutate();
                }
            }
        } catch {
            // Silently fail; user can retry
        }
        setIsAccepting(false);
    };

    if (pendingTexts.length === 0) {
        return null;
    }

    const currentText = pendingTexts[currentIndex];

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col shadow-2xl">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {TYPE_LABELS[currentText.type] || currentText.type}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Please read and accept to continue
                    </p>
                </div>

                <div
                    className="px-6 py-4 overflow-y-auto flex-1 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: currentText.content }}
                />

                <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                        {currentIndex + 1} of {pendingTexts.length}
                    </span>
                    <button
                        className="bg-color1 text-white px-6 py-2 rounded-md font-medium disabled:opacity-50"
                        onClick={handleAccept}
                        disabled={isAccepting}
                    >
                        {isAccepting ? "Accepting..." : "Accept"}
                    </button>
                </div>
            </div>
        </div>
    );
};
